/**
 * promptsVideo — a video that moves through a set of drawings.
 *
 * Each drawing is typeset exactly like its share image (socialExport's
 * renderArtworkCanvas, same format and style), held for a few seconds, then
 * cut — or dissolved — to the next. Encoding is videoEncode's: MP4 through
 * WebCodecs where the browser has it, a real-time recording elsewhere.
 */

import { encodeCanvasVideo } from '@/lib/videoEncode';
import {
  renderArtworkCanvas,
  SIZES,
  type ShareArtwork,
  type SocialFormat,
  type SocialStyle,
} from '@/lib/socialExport';

export interface PromptsVideoOpts {
  /** Seconds each drawing holds. Default 3. */
  hold?: number;
  /** Seconds of dissolve between drawings; 0 for a hard cut. Default 0 — cuts. */
  dissolve?: number;
  fps?: number;
  /** Show the "Prompt-Brush 2.0 / Prompt-Brush.com" line. Default false. */
  header?: boolean;
  onProgress?: (fraction: number) => void;
}

/** Render the selected drawings to a video and trigger the download. Resolves to the file's extension. */
export async function exportPromptsVideo(
  arts: ShareArtwork[],
  format: SocialFormat,
  style: SocialStyle,
  opts: PromptsVideoOpts = {}
): Promise<'mp4' | 'webm'> {
  const { hold = 3, dissolve = 0, fps = 30, header = false, onProgress } = opts;
  if (!arts.length) throw new Error('Pick some drawings first');
  const [W, H] = SIZES[format];

  // 1 · the slides, one canvas each — the same picture the PNG export makes
  const slides: HTMLCanvasElement[] = [];
  for (let i = 0; i < arts.length; i++) {
    slides.push(await renderArtworkCanvas(arts[i], format, style, { header }));
    onProgress?.(((i + 1) / arts.length) * 0.2);
  }

  // 2 · the timeline: hold · (dissolve) · hold …
  const holdF = Math.max(1, Math.round(hold * fps));
  const xfF = Math.max(0, Math.round(dissolve * fps));
  const per = holdF + xfF;
  const total = slides.length * holdF + (slides.length - 1) * xfF;
  const work = document.createElement('canvas');
  work.width = W;
  work.height = H;
  const ctx = work.getContext('2d')!;
  const drawFrame = (n: number) => {
    const i = Math.min(slides.length - 1, Math.floor(n / per));
    const local = n - i * per;
    ctx.globalAlpha = 1;
    ctx.drawImage(slides[i], 0, 0);
    if (xfF > 0 && local >= holdF && i < slides.length - 1) {
      ctx.globalAlpha = (local - holdF + 1) / (xfF + 1);
      ctx.drawImage(slides[i + 1], 0, 0);
      ctx.globalAlpha = 1;
    }
  };

  // 3 · encode
  return encodeCanvasVideo(work, {
    fps,
    total,
    drawFrame,
    name: `prompt-brush-video-${slides.length}-${format}-${style}`,
    onProgress: (f) => onProgress?.(0.2 + f * 0.8),
  });
}
